import { makeObservable, observable, runInAction, computed } from "mobx";
import { RootStore } from "./RootStore";
import { TransportService } from "../services/TransportService";
import DataStore from "./DataStore";
import { MeasurementSaveRequest, Measurement } from "../models/Measurements";
import { getLatestBasedOnDate, sortByDateFunction } from "../utils/dateHelpers";
import { getSymptomsUri } from "../utils/helpers";
import dayjs, { Dayjs } from "dayjs";
import { noteFields, symptomFields } from "../utils/fieldHelpers";

const VACCINATIONS_URL = "/measurements?fields=D_VaccinationType";
const NOTES_URL = "/measurements?fields=D_PERComments,D_PERDrugs,D_PERDiagnos";

class MeasurementStore extends DataStore {
  rootStore: RootStore;
  vaccinations: Measurement[] | undefined = undefined;
  symptoms: Measurement[] | undefined = undefined;
  notes: Measurement[] | undefined = undefined;
  daySymptoms: Map<string, string[]> | undefined = undefined;
  symptomDays: Map<string, Dayjs[]> | undefined = undefined;
  dayNotes: Map<string, Measurement[]> | undefined = undefined;

  constructor(transportService: TransportService, rootStore: RootStore) {
    super(transportService);
    this.rootStore = rootStore;

    makeObservable(this, {
      vaccinations: observable,
      symptoms: observable,
      daySymptoms: observable,
      symptomDays: observable,
      notes: observable,
      dayNotes: observable,
      latestSymptoms: computed,
      latestVaccinations: computed,
      latestNotes: computed,
      success: observable,
      error: observable,
    });
  }

  setVaccinations(vacs: Measurement[] | undefined) {
    runInAction(() => {
      this.vaccinations = vacs ?? [];
    });
  }

  setNotes(notes: Measurement[] | undefined) {
    const dayNotes = new Map<string, Measurement[]>();
    if (notes) {
      notes.forEach((n) => {
        const date = dayjs(n.date).format("YYYY-MM-DD");
        const arr = dayNotes.get(date) || [];
        arr.push(n);
        dayNotes.set(date, arr);
      });
    }
    runInAction(() => {
      this.notes = notes ?? [];
      this.dayNotes = dayNotes;
    });
  }

  get latestVaccinations() {
    if (this.vaccinations) {
      return getLatestBasedOnDate(this.vaccinations) as Measurement[];
    }
    return [] as Measurement[];
  }

  get latestSymptoms() {
    if (this.symptoms) {
      return getLatestBasedOnDate(this.symptoms) as Measurement[];
    }
    return [] as Measurement[];
  }

  get latestNotes() {
    if (this.notes) {
      return getLatestBasedOnDate(this.notes) as Measurement[];
    }
    return [] as Measurement[];
  }

  getSymptomsByDate = (date: Dayjs) => {
    if (this.symptoms) {
      return this.symptoms.filter(
        (s) => dayjs(s.date).format("YYYY-MM-DD") === date.format("YYYY-MM-DD")
      );
    }
    return [] as Measurement[];
  };

  setSymptoms(symps: Measurement[] | undefined) {
    const daySymptoms = new Map<string, string[]>();
    const symptomDays = new Map<string, Dayjs[]>();
    if (symps) {
      symps.forEach((s) => {
        const dateStr = s.date.toString().substring(0, 10);
        const arr = daySymptoms.get(dateStr) || [];
        const arr2 = symptomDays.get(s.fieldAlias) || [];
        if (!arr.includes(s.fieldAlias)) {
          arr.push(s.fieldAlias);
        }
        if (!arr2.includes(s.date)) {
          arr2.push(s.date);
        }
        daySymptoms.set(dateStr, arr);
        symptomDays.set(s.fieldAlias, arr2);
      });
    }
    runInAction(() => {
      this.symptoms = symps ?? [];
      this.daySymptoms = daySymptoms;
      this.symptomDays = symptomDays;
    });
  }

  getVaccinations = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getVaccinations");
      this.setLoading("getVaccinations");
      const res = await this.transportService.getWithToken(
        VACCINATIONS_URL,
        token
      );
      const sortedVacs = res.data.measurements.sort(
        sortByDateFunction((v: Measurement) => v.date)
      );
      this.setVaccinations(sortedVacs);
    } catch (e) {
      console.error(e);
      this.setError("getVaccinations");
      return;
    } finally {
      this.clearLoading("getVaccinations");
    }
  };

  getNotes = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getNotes");
      this.setLoading("getNotes");
      const res = await this.transportService.getWithToken(NOTES_URL, token);
      const sortedNotes = res.data.measurements.sort(
        sortByDateFunction((m: Measurement) => m.date)
      );
      this.setNotes(sortedNotes);
    } catch (e) {
      console.error(e);
      this.setError("getNotes");
      return;
    } finally {
      this.clearLoading("getNotes");
    }
  };

  getSymptoms = async () => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("getSymptoms");
      this.setLoading("getSymptoms");
      const res = await this.transportService.getWithToken(
        getSymptomsUri(),
        token
      );
      const sortedSymptoms = res.data.measurements.sort(
        sortByDateFunction((m: Measurement) => m.date)
      );
      this.setSymptoms(sortedSymptoms);
    } catch (e) {
      console.error(e);
      this.setError("getSymptoms");
      return;
    } finally {
      this.clearLoading("getSymptoms");
    }
  };

  saveMeasurement = async (measurementSaveRequest: MeasurementSaveRequest) => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("saveMeasurement");
      this.setLoading("saveMeasurement");
      await this.transportService.postWithToken(
        measurementSaveRequest,
        "/measurements",
        token
      );
      this.setSuccess("saveMeasurement");
      if (measurementSaveRequest.fieldAlias === "D_VaccinationType") {
        this.getVaccinations();
      }
      if (noteFields.includes(measurementSaveRequest.fieldAlias)) {
        this.getNotes();
      }
      if (symptomFields.includes(measurementSaveRequest.fieldAlias)) {
        this.getSymptoms();
      }
    } catch (e) {
      console.error(e);
      this.setError("saveMeasurement");
      return;
    } finally {
      this.clearLoading("saveMeasurement");
    }
  };

  updateMeasurement = async (
    measurementId: number,
    measurementSaveRequest: MeasurementSaveRequest
  ) => {
    const token = await this.rootStore.keycloakStore.getRefreshedToken();
    if (!token) {
      return;
    }
    try {
      this.clearError("updateMeasurement");
      this.setLoading("updateMeasurement");
      await this.transportService.putWithToken(
        measurementSaveRequest,
        `/measurements/${measurementId}`,
        token
      );
      this.setSuccess("updateMeasurement");
      if (measurementSaveRequest.fieldAlias === "D_VaccinationType") {
        this.getVaccinations();
      }
      if (noteFields.includes(measurementSaveRequest.fieldAlias)) {
        this.getNotes();
      }
      if (symptomFields.includes(measurementSaveRequest.fieldAlias)) {
        this.getSymptoms();
      }
    } catch (e) {
      console.error(e);
      this.setError("updateMeasurement");
      return;
    } finally {
      this.clearLoading("updateMeasurement");
    }
  };
}

export default MeasurementStore;
